import { Injectable } from '@angular/core';
import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Default region used to parse bare national numbers (no country code).
 * US-only for now; international collection is intentionally out of scope.
 * Adding a region is a config change here, not a refactor of every form.
 */
export const DEFAULT_PHONE_REGION: CountryCode = 'US';

/** Validation error key emitted by {@link PhoneValidationService.phoneValidator}. */
export const INVALID_PHONE_ERROR = 'invalidPhone';

/**
 * Single source of truth for phone-number validation and E.164 normalization
 * across every Vision form that collects a phone number (email-form,
 * exit-intent-modal, phone-capture, and any future surface).
 *
 * Wraps `libphonenumber-js` so callers never duplicate regex or per-component
 * logic. Validation rejects obviously invalid numbers (all-zeros, sequential,
 * 555 area codes, invalid US area codes) and any non-US number; normalization
 * is gated on validation so garbage is never normalized.
 */
@Injectable({
  providedIn: 'root',
})
export class PhoneValidationService {
  /**
   * Returns true only for a number that `libphonenumber-js` parses as valid
   * AND resolves to the target region (US by default). Foreign numbers and
   * other NANP countries (e.g. Canada) are rejected to keep the list US-only.
   *
   * Non-string, empty, and whitespace-only inputs are treated as invalid here;
   * use {@link phoneValidator} for optional form controls where emptiness is
   * allowed and `Validators.required` governs presence.
   *
   * @param input - Raw user-typed value in any common format.
   * @param region - Region to assume for bare national numbers. Defaults to US.
   */
  isValid(input: unknown, region: CountryCode = DEFAULT_PHONE_REGION): boolean {
    if (typeof input !== 'string') {
      return false;
    }
    const trimmed = input.trim();
    if (!trimmed) {
      return false;
    }
    const parsed = parsePhoneNumberFromString(trimmed, region);
    return !!parsed && parsed.isValid() && parsed.country === region;
  }

  /**
   * Normalizes a valid number to its E.164 representation (e.g. `+15125550123`).
   * Returns null for any input that fails {@link isValid} — normalization is
   * always gated on validation, because the library will happily normalize
   * invalid garbage (e.g. `0000000000` -> `+10000000000`) if asked directly.
   *
   * The extension, if present, is dropped from the returned base E.164 string.
   *
   * @param input - Raw user-typed value in any common format.
   * @param region - Region to assume for bare national numbers. Defaults to US.
   */
  normalizeToE164(input: unknown, region: CountryCode = DEFAULT_PHONE_REGION): string | null {
    if (typeof input !== 'string') {
      return null;
    }
    const trimmed = input.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = parsePhoneNumberFromString(trimmed, region);
    if (!parsed || !parsed.isValid() || parsed.country !== region) {
      return null;
    }
    return parsed.number;
  }

  /**
   * Angular validator function for reactive phone form controls.
   *
   * Truly empty values (null/undefined/'') pass (returns null) so the control
   * stays optional — pair with `Validators.required` where a phone number is
   * mandatory. Any other value (including whitespace-only) must be a valid phone
   * or it produces `{ [INVALID_PHONE_ERROR]: true }`. Whitespace-only is treated
   * as invalid rather than optional so the control's validity never disagrees
   * with `Validators.required` (which counts whitespace as present).
   *
   * @param region - Region to assume for bare national numbers. Defaults to US.
   */
  phoneValidator(region: CountryCode = DEFAULT_PHONE_REGION): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: unknown = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      return this.isValid(value, region) ? null : { [INVALID_PHONE_ERROR]: true };
    };
  }
}
